import { configureStore, combineReducers } from '@reduxjs/toolkit';
import createSagaMiddleware from 'redux-saga';
import logger from 'redux-logger';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { PersistConfig } from 'redux-persist/lib/types';
import appReducer from '../containers/App/reducer';
import { setToken } from '../containers/App/actions';
import posDataReducer from '../containers/posData/reducer';
import tasksReducer from '../containers/Tasks/reducer';
import rootSaga from './rootSaga';
import { setAuthSync } from '../services/api';
import { setDateFormat as syncDateFormatModule } from '../lib/dateFormatState';

const staticReducers = {
  app: appReducer,
  posData: posDataReducer,
  tasks: tasksReducer,
};

const sagaMiddleware = createSagaMiddleware();

const persistConfig: PersistConfig<any> = {
  key: 'sajilo-pos-v4',
  storage,
  whitelist: ['posData', 'app'],
};

function createReducer(injectedReducers = {}) {
  const rootReducer = {
    ...staticReducers,
    ...injectedReducers,
  };

  if (Object.keys(rootReducer).length === 0) {
    return (
      state = {
        _persist: { version: persistConfig.version ?? -1, rehydrated: false },
      },
    ) => state;
  }

  return persistReducer(
    persistConfig,
    combineReducers(rootReducer as Partial<typeof rootReducer>) as any,
  );
}

export function configureAppStore(
  initialState = { _persist: { version: -1, rehydrated: false } },
) {
  const store = configureStore({
    reducer: createReducer(),
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
        },
      }).concat(sagaMiddleware, logger) as any,
    devTools: process.env.NODE_ENV !== 'production',
  });

  (store as any).injectedReducers = {} as Record<string, any>;
  (store as any).injectedSagas = {} as Record<string, any>;
  (store as any).runSaga = sagaMiddleware.run;
  (store as any).runSaga(rootSaga);

  (store as any).injectReducer = (key: string, reducer: any) => {
    if ((store as any).injectedReducers[key]) {
      return;
    }
    (store as any).injectedReducers[key] = reducer;
    store.replaceReducer(createReducer((store as any).injectedReducers) as any);
  };

  (store as any).injectSaga = (key: string, saga: any) => {
    const hasSaga = Boolean((store as any).injectedSagas[key]);
    if (hasSaga) {
      (store as any).injectedSagas[key].task.cancel();
      (store as any).injectedSagas[key].task.toPromise().catch(() => {});
    }
    const task = (store as any).runSaga(saga);
    (store as any).injectedSagas[key] = { saga, task };
    return task;
  };

  (store as any).ejectReducer = (key: string) => {
    if (!(store as any).injectedReducers[key]) {
      return;
    }
    delete (store as any).injectedReducers[key];
    store.replaceReducer(createReducer((store as any).injectedReducers) as any);
  };

  (store as any).ejectSaga = (key: string) => {
    if (!(store as any).injectedSagas[key]) {
      return;
    }
    (store as any).injectedSagas[key].task.cancel();
    (store as any).injectedSagas[key].task.toPromise().catch(() => {});
    delete (store as any).injectedSagas[key];
  };

  const persistor = persistStore(store);
  (store as any).persistor = persistor;

  // Keep the redux token in sync when the API client auto-refreshes the
  // access token underneath an in-flight request.
  setAuthSync((token) => store.dispatch(setToken(token)));

  // todayLabel()/formatDate() read the dateFormatState module, not redux.
  // Mirror the persisted posData.dateFormat into it on every change so the
  // toggle (and rehydration after reload) actually changes displayed dates.
  let lastSyncedFormat: string | null = null;
  store.subscribe(() => {
    const format = (store.getState() as any)?.posData?.dateFormat;
    if ((format === 'BS' || format === 'AD') && format !== lastSyncedFormat) {
      lastSyncedFormat = format;
      syncDateFormatModule(format);
    }
  });

  return { store, persistor } as const;
}
